import React, { useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Chip,
  Grid,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  Star as StarIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  FlashOn as FlashOnIcon,
  AccessTime as AccessTimeIcon,
  Language as LanguageIcon,
  Place as PlaceIcon,
  CalendarToday as CalendarTodayIcon,
} from "@mui/icons-material";

// Interfaces for better IntelliSense (not enforced at runtime)
/** @typedef {{ id: string; startTime: string; endTime: string; enabled: boolean }} TimeSlot */
/**
 * @typedef {Object} BookingTemplate
 * @property {string} id
 * @property {string} name
 * @property {"WEEKLY"|"MONTHLY"} type
 * @property {TimeSlot[]} timeSlots
 * @property {string} createdAt
 * @property {"ONLINE"|"OFFLINE"|"BOTH"} bookingType
 * @property {number=} maxAdvanceBookingDays
 * @property {number=} minAdvanceBookingHours
 * @property {string=} description
 * @property {boolean} isDefault
 * @property {boolean} autoGenerateSlots
 * @property {{ daysInAdvance:number; generateUntilDate?:string; selectedDays:string[]; lastGenerated?:string }=} autoGenerationSettings
 */

const initialTemplates = [
  {
    id: "1",
    name: "Doctor Consultation",
    type: "WEEKLY",
    timeSlots: [
      { id: "1", startTime: "09:00", endTime: "10:00", enabled: true },
      { id: "2", startTime: "10:00", endTime: "11:00", enabled: true },
      { id: "3", startTime: "14:00", endTime: "15:00", enabled: true },
    ],
    createdAt: "2024-01-15",
    bookingType: "BOTH",
    maxAdvanceBookingDays: 30,
    minAdvanceBookingHours: 24,
    description: "General medical consultation",
    isDefault: true,
    autoGenerateSlots: true,
    autoGenerationSettings: {
      daysInAdvance: 14,
      selectedDays: ["monday", "tuesday", "wednesday", "thursday", "friday"],
      lastGenerated: "2024-01-20",
    },
  },
  {
    id: "2",
    name: "Legal Consultation",
    type: "MONTHLY",
    timeSlots: [
      { id: "4", startTime: "10:00", endTime: "12:00", enabled: true },
      { id: "5", startTime: "14:00", endTime: "16:00", enabled: true },
    ],
    createdAt: "2024-01-10",
    bookingType: "ONLINE",
    maxAdvanceBookingDays: 60,
    minAdvanceBookingHours: 48,
    description: "Legal advice and consultation",
    isDefault: false,
    autoGenerateSlots: false,
  },
  {
    id: "3",
    name: "Walk-in Clinic",
    type: "WEEKLY",
    timeSlots: [
      { id: "6", startTime: "08:00", endTime: "09:00", enabled: true },
      { id: "7", startTime: "09:00", endTime: "10:00", enabled: true },
      { id: "8", startTime: "10:00", endTime: "11:00", enabled: true },
    ],
    createdAt: "2024-01-12",
    bookingType: "OFFLINE",
    description: "Walk-in appointments only",
    isDefault: false,
    autoGenerateSlots: true,
    autoGenerationSettings: {
      daysInAdvance: 7,
      selectedDays: ["monday", "wednesday", "friday"],
      lastGenerated: "2024-01-19",
    },
  },
];

const defaultFormState = {
  name: "",
  type: "WEEKLY",
  timeSlots: [{ id: "temp1", startTime: "09:00", endTime: "10:00", enabled: true }],
  bookingType: "BOTH",
  maxAdvanceBookingDays: 30,
  minAdvanceBookingHours: 24,
  description: "",
  isDefault: false,
  autoGenerateSlots: false,
  autoGenerationSettings: {
    daysInAdvance: 14,
    generateUntilDate: "",
    selectedDays: [],
  },
};

function Templates() {
  const { t } = useLanguage();
  const [templates, setTemplates] = useState(initialTemplates);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState(defaultFormState);

  const resetForm = () => {
    setFormData(defaultFormState);
    setEditingTemplate(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSlotChange = (index, key, value) => {
    setFormData((prev) => {
      const ts = prev.timeSlots.map((s, i) => (i === index ? { ...s, [key]: value } : s));
      return { ...prev, timeSlots: ts };
    });
  };

  const addTimeSlot = () => {
    setFormData((prev) => ({
      ...prev,
      timeSlots: [
        ...prev.timeSlots,
        { id: `temp${Date.now()}`, startTime: "09:00", endTime: "10:00", enabled: true },
      ],
    }));
  };

  const removeTimeSlot = (index) => {
    setFormData((prev) => ({
      ...prev,
      timeSlots: prev.timeSlots.filter((_, i) => i !== index),
    }));
  };

  const saveTemplate = () => {
    if (editingTemplate) {
      setTemplates((prev) =>
        prev.map((t) => (t.id === editingTemplate.id ? { ...editingTemplate, ...formData } : t))
      );
    } else {
      setTemplates((prev) => [
        ...prev,
        {
          ...formData,
          id: Date.now().toString(),
          createdAt: new Date().toISOString().split("T")[0],
          timeSlots: formData.timeSlots.map((slot, i) => ({ ...slot, id: `${Date.now()}-${i}` })),
        },
      ]);
    }
    setDialogOpen(false);
  };

  const editTemplate = (tpl) => {
    setEditingTemplate(tpl);
    setFormData({ ...tpl });
    setDialogOpen(true);
  };

  const deleteTemplate = (id) => {
    setTemplates((prev) => prev.filter((t) => t.id !== id));
  };

  const setAsDefault = (id) => {
    setTemplates((prev) =>
      prev.map((t) => ({ ...t, isDefault: t.id === id }))
    );
    alert("Default template updated");
  };

  const toggleAuto = (id) => {
    setTemplates((prev) =>
      prev.map((t) => (t.id === id ? { ...t, autoGenerateSlots: !t.autoGenerateSlots } : t))
    );
  };

  const simulateCronJob = () => {
    const autoTemplates = templates.filter((t) => t.autoGenerateSlots);
    let total = 0;
    autoTemplates.forEach((t) => {
      if (t.autoGenerationSettings) {
        const slotsPerDay = t.timeSlots.filter((s) => s.enabled).length;
        const days = t.autoGenerationSettings.selectedDays.length || 1;
        total += slotsPerDay * days * t.autoGenerationSettings.daysInAdvance;
      }
    });
    alert(`Cron simulation: generated ${total} slots from ${autoTemplates.length} templates`);
  };

  const bookingTypeChip = (type) => {
    const map = {
      ONLINE: { label: "ONLINE", color: "primary", icon: <LanguageIcon fontSize="small" /> },
      OFFLINE: { label: "OFFLINE", color: "default", icon: <PlaceIcon fontSize="small" /> },
      BOTH: { label: "BOTH", color: "success", icon: <CalendarTodayIcon fontSize="small" /> },
    };
    const cfg = map[type] || map.BOTH;
    return (
      <Chip
        icon={cfg.icon}
        label={cfg.label}
        size="small"
        color={cfg.color}
        sx={{ mr: 0.5 }}
      />
    );
  };

  return (
    <Stack spacing={3} sx={{ p: 2, mt:5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <Typography variant="h4">{t("pages.templates.title")}</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<FlashOnIcon />} onClick={simulateCronJob}>
            Simulate Cron
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            Add Template
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={2}>
        {templates.map((tpl) => (
          <Grid item xs={12} md={6} lg={4} key={tpl.id}>
            <Card variant="outlined">
              <CardHeader
                title={
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1">{tpl.name}</Typography>
                    {tpl.isDefault && (
                      <Chip icon={<StarIcon fontSize="small" />} label="Default" color="warning" size="small" />
                    )}
                  </Stack>
                }
                subheader={`Created on ${new Date(tpl.createdAt).toLocaleDateString()}`}
              />
              <CardContent>
                <Stack spacing={1}>
                  {tpl.description && <Typography variant="body2">{tpl.description}</Typography>}
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label={tpl.type} size="small" />
                    {bookingTypeChip(tpl.bookingType)}
                    {tpl.autoGenerateSlots && (
                      <Chip icon={<FlashOnIcon fontSize="small" />} label="Auto" size="small" color="success" />
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AccessTimeIcon fontSize="small" />
                    <Typography variant="body2">
                      {tpl.timeSlots.filter((s) => s.enabled).length} active time slots
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
              <CardActions sx={{ justifyContent: "space-between" }}>
                <Stack direction="row" spacing={1}>
                  {!tpl.isDefault && (
                    <Tooltip title="Set as default">
                      <IconButton onClick={() => setAsDefault(tpl.id)} size="small" color="warning">
                        <StarIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Toggle auto generation">
                    <IconButton
                      onClick={() => toggleAuto(tpl.id)}
                      size="small"
                      color={tpl.autoGenerateSlots ? "success" : "default"}
                    >
                      <FlashOnIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Edit">
                    <IconButton onClick={() => editTemplate(tpl)} size="small" color="primary">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton onClick={() => deleteTemplate(tpl.id)} size="small" color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingTemplate ? "Edit Template" : "Create Template"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Template Name"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              required
            />
            <FormControl>
              <InputLabel>Template Type</InputLabel>
              <Select
                label="Template Type"
                value={formData.type}
                onChange={(e) => handleChange("type", e.target.value)}
              >
                <MenuItem value="WEEKLY">Weekly</MenuItem>
                <MenuItem value="MONTHLY">Monthly</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
            />
            <FormControl>
              <InputLabel>Booking Type</InputLabel>
              <Select
                label="Booking Type"
                value={formData.bookingType}
                onChange={(e) => handleChange("bookingType", e.target.value)}
              >
                <MenuItem value="ONLINE">Online</MenuItem>
                <MenuItem value="OFFLINE">Offline</MenuItem>
                <MenuItem value="BOTH">Both</MenuItem>
              </Select>
            </FormControl>
            {(formData.bookingType === "ONLINE" || formData.bookingType === "BOTH") && (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Max Advance Booking Days"
                  type="number"
                  value={formData.maxAdvanceBookingDays}
                  onChange={(e) => handleChange("maxAdvanceBookingDays", Number(e.target.value))}
                />
                <TextField
                  label="Min Advance Notice Hours"
                  type="number"
                  value={formData.minAdvanceBookingHours}
                  onChange={(e) => handleChange("minAdvanceBookingHours", Number(e.target.value))}
                />
              </Stack>
            )}

            {/* Auto-gen & default */}
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isDefault}
                    onChange={(e) => handleChange("isDefault", e.target.checked)}
                  />
                }
                label="Set as default template"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.autoGenerateSlots}
                    onChange={(e) => handleChange("autoGenerateSlots", e.target.checked)}
                  />
                }
                label="Enable automatic slot generation"
              />
            </FormGroup>

            {/* Time slots */}
            <Stack spacing={1}>
              <Typography variant="subtitle1">Time Slots</Typography>
              {formData.timeSlots.map((slot, idx) => (
                <Stack direction="row" spacing={1} alignItems="center" key={slot.id}>
                  <Checkbox
                    checked={slot.enabled}
                    onChange={(e) => handleSlotChange(idx, "enabled", e.target.checked)}
                  />
                  <TextField
                    type="time"
                    value={slot.startTime}
                    onChange={(e) => handleSlotChange(idx, "startTime", e.target.value)}
                  />
                  <Typography variant="body2">to</Typography>
                  <TextField
                    type="time"
                    value={slot.endTime}
                    onChange={(e) => handleSlotChange(idx, "endTime", e.target.value)}
                  />
                  {formData.timeSlots.length > 1 && (
                    <IconButton size="small" onClick={() => removeTimeSlot(idx)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              ))}
              <Button startIcon={<AddIcon />} onClick={addTimeSlot} variant="outlined" size="small">
                Add Time Slot
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={saveTemplate} variant="contained">
            {editingTemplate ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

export default Templates;
